(() => {
  if (customElements.get('mn-preorders')) return;

  const query = 'query PreorderProducts($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) { nodes(ids: $ids) { ... on ProductVariant { id product { id handle } } } }';
  const concurrency = 3;
  const batchSize = 250;

  class MnPreorders extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.grid = this.querySelector('[data-mn-preorder-grid]');
        this.status = this.querySelector('[data-mn-preorder-status]');
        this.empty = this.querySelector('[data-mn-preorder-empty]');
        this.error = this.querySelector('[data-mn-preorder-error]');
        this.more = this.querySelector('[data-mn-preorder-more]');
        this.retryButton = this.querySelector('[data-mn-preorder-retry]');
        this.moreButton = this.querySelector('[data-mn-preorder-load-more]');
        this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 24);
        this.visibleLimit = this.pageSize;
        this.entries = [];
        this.controllers = new Set();
        this.operation = 0;
        this.discovered = false;
        this.failedDiscovery = false;
        this.labels = this.readJSON('[data-mn-preorder-labels]', {});
        const arrays = this.readJSON('[data-mn-preorder-ids]', []);
        this.variantIds = Array.from(new Set((Array.isArray(arrays) ? arrays.flat(Infinity) : [])
          .map((id) => String(id))
          .filter((id) => /^\d+$/.test(id))));
        this.retryButton.addEventListener('click', () => this.run(true));
        this.moreButton.addEventListener('click', () => {
          this.visibleLimit += this.pageSize;
          this.run();
        });
      }
      this.run();
    }

    disconnectedCallback() {
      this.operation += 1;
      this.controllers.forEach((controller) => controller.abort());
      this.busy = false;
      this.entries.forEach((entry) => {
        if (entry.state === 'loading') entry.state = 'pending';
      });
    }

    readJSON(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || 'null') ?? fallback;
      } catch {
        return fallback;
      }
    }

    async request(url, options, parser) {
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`Preorder request failed: ${response.status}`);
        return await response[parser]();
      } finally {
        window.clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    async discover(operation) {
      const batches = [];
      for (let index = 0; index < this.variantIds.length; index += batchSize) {
        batches.push(this.variantIds.slice(index, index + batchSize));
      }
      const results = new Array(batches.length);
      let cursor = 0;
      const worker = async () => {
        while (cursor < batches.length) {
          const index = cursor++;
          const result = await this.request('/api/2026-07/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ query, variables: { ids: batches[index].map((id) => `gid://shopify/ProductVariant/${id}`), country: this.dataset.country } })
          }, 'json');
          if (operation !== this.operation) return;
          if (result.errors?.length || !Array.isArray(result.data?.nodes)) throw new Error('Preorder lookup failed');
          results[index] = result.data.nodes;
        }
      };
      const completed = await Promise.allSettled(Array.from({ length: Math.min(concurrency, batches.length) }, worker));
      if (operation !== this.operation) return;
      if (completed.some((result) => result.status === 'rejected')) throw new Error('Preorder lookup incomplete');
      const seen = new Set();
      this.entries = results.flat().flatMap((variant) => {
        const product = variant?.product;
        if (!product?.id || !product.handle || seen.has(product.id)) return [];
        seen.add(product.id);
        return [{ id: product.id, handle: product.handle, state: 'pending', element: null }];
      });
      this.discovered = true;
      this.failedDiscovery = false;
    }

    productURL(handle) {
      const root = this.dataset.localeRoot || window.Shopify?.routes?.root || '/';
      const url = new URL(`${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'mn-preorder-card');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async loadEntry(entry, operation) {
      entry.state = 'loading';
      try {
        const html = await this.request(this.productURL(entry.handle), { headers: { Accept: 'text/html' }, cache: 'no-store' }, 'text');
        if (operation !== this.operation) return;
        const response = new DOMParser().parseFromString(html, 'text/html');
        const result = response.querySelector('[data-mn-preorder-card]');
        if (!result || !['true', 'false'].includes(result.dataset.mnPreorderEligible)) throw new Error('Preorder card missing');
        const productId = entry.id.split('/').pop();
        if (result.dataset.mnPreorderProduct !== productId) throw new Error('Preorder product mismatch');
        if (result.dataset.mnPreorderEligible === 'false') {
          entry.state = 'empty';
          return;
        }
        const card = result.querySelector('product-card');
        if (!card) throw new Error('Preorder card missing');
        const item = document.createElement('li');
        item.className = 'mn-preorders__item';
        item.dataset.mnPreorderIndex = String(this.entries.indexOf(entry));
        item.append(document.importNode(card, true));
        entry.element = item;
        entry.state = 'ready';
      } catch {
        if (operation === this.operation) entry.state = 'failed';
      }
    }

    readyCount() {
      return this.entries.filter((entry) => entry.state === 'ready').length;
    }

    renderCards() {
      let shown = 0;
      this.entries.forEach((entry) => {
        if (entry.state !== 'ready') return;
        if (entry.element.parentNode !== this.grid) {
          const index = Number(entry.element.dataset.mnPreorderIndex);
          const next = Array.from(this.grid.children).find((element) => Number(element.dataset.mnPreorderIndex) > index);
          this.grid.insertBefore(entry.element, next || null);
        }
        entry.element.hidden = shown >= this.visibleLimit;
        shown += 1;
      });
    }

    async loadCards(operation, retry) {
      if (retry) {
        const failed = this.entries.filter((entry) => entry.state === 'failed');
        for (let index = 0; index < failed.length; index += concurrency) {
          await Promise.all(failed.slice(index, index + concurrency).map((entry) => this.loadEntry(entry, operation)));
          if (operation !== this.operation) return;
          this.renderCards();
        }
      }
      while (this.readyCount() < this.visibleLimit) {
        const pending = this.entries.filter((entry) => entry.state === 'pending').slice(0, concurrency);
        if (!pending.length) break;
        await Promise.all(pending.map((entry) => this.loadEntry(entry, operation)));
        if (operation !== this.operation) return;
        this.renderCards();
      }
      this.renderCards();
    }

    updateState() {
      const failed = this.failedDiscovery || this.entries.some((entry) => entry.state === 'failed');
      const remaining = this.entries.some((entry) => entry.state === 'pending');
      const count = this.readyCount();
      this.setAttribute('aria-busy', String(this.busy));
      this.retryButton.disabled = this.busy;
      this.moreButton.disabled = this.busy;
      this.error.hidden = this.busy || !failed;
      this.empty.hidden = this.busy || failed || !this.discovered || remaining || count > 0;
      this.more.hidden = this.busy || !this.discovered || (!remaining && count <= this.visibleLimit);
      this.status.textContent = this.busy ? this.labels.loading || '' : (!failed && !remaining && count > 0 ? (this.labels.count || '').replace('__COUNT__', String(count)) : '');
    }

    async run(retry = false) {
      if (this.busy || !this.isConnected) return;
      const operation = ++this.operation;
      this.busy = true;
      this.updateState();
      try {
        if (!this.discovered) await this.discover(operation);
        if (operation !== this.operation) return;
        await this.loadCards(operation, retry);
      } catch {
        if (operation === this.operation) this.failedDiscovery = !this.discovered;
      } finally {
        if (operation === this.operation) {
          this.busy = false;
          this.updateState();
        }
      }
    }
  }

  customElements.define('mn-preorders', MnPreorders);
})();
